import { Fragment } from 'react';
import {
  DragDropContext,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { UseFieldArrayMove, UseFieldArrayRemove, UseFormSetValue } from 'react-hook-form';
import { BORDER_RADIUSES, COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import GraphColoringRuleRow from 'dashboard/components/FormControls/GraphColoringRulesField/GraphColoringRuleRow';
import HoverableDraggable from 'dashboard/components/FormControls/GraphColoringRulesField/HoverableDraggable';
import { GraphAttributes } from 'dashboard/models/graph';
import { ServerColorRule } from 'dashboard/models/graph-backend';
import { MenuDivider } from 'design-system/components/Menu';

interface GraphColoringRulesFieldProps {
  coloringRules: ServerColorRule[];
  move: UseFieldArrayMove;
  setValue: UseFormSetValue<GraphAttributes>;
  removeColorRule: UseFieldArrayRemove;
  onGraphConfigEdit: () => void;
}

const ColorRuleList = styled.div<{ isDraggingOver: boolean }>`
  ${({ isDraggingOver }) => css`
    background-color: ${isDraggingOver ? COLORS.indigo10 : COLORS.white};
  `};
  transition:
    background-color 0.4s ease-in-out,
    box-shadow 0.4s ease-in-out;
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const Divider = styled(MenuDivider)`
  margin: 0;
`;

const ItemContainer = styled.section`
  flex: 1;
`;

export const GraphColoringRulesField = ({
  coloringRules,
  move,
  setValue,
  removeColorRule,
  onGraphConfigEdit,
}: GraphColoringRulesFieldProps) => {
  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    onGraphConfigEdit();
    move(result.source.index, result.destination.index);
  };

  const renderRouteSections = (
    droppableProvided: DroppableProvided,
    droppableSnapshot: DroppableStateSnapshot,
  ) => (
    <ColorRuleList
      ref={droppableProvided.innerRef}
      isDraggingOver={droppableSnapshot.isDraggingOver}
    >
      {coloringRules.map((rule, index) => {
        return (
          <Fragment key={index}>
            <HoverableDraggable draggableId={index.toString()} index={index} isDragDisabled={false}>
              <ItemContainer>
                <GraphColoringRuleRow
                  colorRule={rule}
                  setValue={setValue}
                  index={index}
                  removeColorRule={removeColorRule}
                  onGraphConfigEdit={onGraphConfigEdit}
                />
              </ItemContainer>
            </HoverableDraggable>
            <Divider />
          </Fragment>
        );
      })}
      {droppableProvided.placeholder}
    </ColorRuleList>
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId='droppable'>{renderRouteSections}</Droppable>
    </DragDropContext>
  );
};

export default GraphColoringRulesField;
